<!-- app\javascript\dashboard\routes\dashboard\bulkmessage\components\MessageComposer.vue -->

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useStore } from 'vuex';
import Button from 'dashboard/components-next/button/Button.vue';
import MessagePreview from './MessagePreview.vue';
import AlertResponza from 'dashboard/components-next/responza/AlertResponza.vue';
import WhatsAppEditor from './WhatsAppEditor.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  attachments: {
    type: Array,
    default: () => [],
  },
  preview: {
    type: Boolean,
    default: false,
  },
  recipients: {
    type: Array,
    default: () => [],
  },
  hidePreviewButton: {
    type: Boolean,
    default: false,
  },
  additionalColumns: {
    type: Array,
    default: () => [],
  },
  initialVariationsState: {
    type: Object,
    default: () => ({ generatedVariations: [], selectedVariations: [] }),
  },
  isDraggingFiles: {
    type: Boolean,
    default: false,
  },
  hasMessengerInbox: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:modelValue',
  'file-upload',
  'remove-attachment',
  'toggle-preview',
]);

const store = useStore();

const fileSizeLimit = computed(() => {
  return store.getters['globalConfig/get'].fileSizeLimit || 20; // Default to 20MB
});

const message = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const isImageFile = type => {
  return type.startsWith('image/');
};

const isVideoFile = type => {
  return type.startsWith('video/');
};

const isAudioFile = type => {
  return type.startsWith('audio/');
};

const isDraggingOver = ref(false);
const showTemplateVariables = ref(false);
const showEmojiPicker = ref(false);
const isRewritingWithAI = ref(false);
const showAIApprovalModal = ref(false);
const aiRewrittenText = ref('');
const isGeneratingVariations = ref(false);
const variationsRetryCount = ref(0);
const maxRetries = 3;
// Initialize with saved state if available
const generatedVariations = ref(props.initialVariationsState?.generatedVariations || []);
const selectedVariations = ref(props.initialVariationsState?.selectedVariations || []);
const editingVariation = ref(null);
const showTooltip = ref(false);

// Alert modal state
const showFileSizeAlert = ref(false);
const fileSizeAlertMessage = ref('');

// Audio confirmation modal state
const showAudioConfirmModal = ref(false);
const pendingAudioFile = ref(null);

// Variation warning modal state
const showVariationWarning = ref(false);
const acceptedTerms = ref(false);
const canAcceptTerms = ref(false);
const termsCountdown = ref(5);
let termsTimer = null;

// Preview variation tracking
const previewingVariationId = ref(null);

const startTermsCountdown = () => {
  canAcceptTerms.value = false;
  termsCountdown.value = 5;
  termsTimer = setInterval(() => {
    termsCountdown.value--;
    if (termsCountdown.value <= 0) {
      clearInterval(termsTimer);
      termsTimer = null;
      canAcceptTerms.value = true;
    }
  }, 1000);
};

const clearTermsTimer = () => {
  if (termsTimer) {
    clearInterval(termsTimer);
    termsTimer = null;
  }
  canAcceptTerms.value = false;
  termsCountdown.value = 5;
};

onBeforeUnmount(() => {
  clearTermsTimer();
});

// WhatsApp Editor handles all text editing and variables

// Check if any attachment is an audio file
const hasAudioAttachment = computed(() => {
  return props.attachments.some(attachment => isAudioFile(attachment.type));
});

const templateVariables = computed(() => {
  const baseVariables = [
    {
      name: `{{nome}}`,
      description: 'Nome do contato (primeira parte)',
    },
    {
      name: `{{sobrenome}}`,
      description: 'Sobrenome do contato (segunda parte)',
    },
    { name: `{{nome_completo}}`, description: 'Nome completo do contato' },
    { name: `{{telefone}}`, description: 'Número de telefone do contato' },
  ];

  // Get custom fields from recipients (any column that's not a standard field)
  const customFieldsFromRecipients = new Set();
  if (props.recipients && props.recipients.length > 0) {
    props.recipients.forEach(recipient => {
      if (recipient.customFields) {
        Object.keys(recipient.customFields).forEach(field => {
          // Exclude DDI as per user request
          const lowerField = field.toLowerCase();
          if (!lowerField.includes('ddi') && !lowerField.includes('country')) {
            customFieldsFromRecipients.add(field);
          }
        });
      }
    });
  }

  // Combine additional columns with custom fields from recipients
  const allCustomFields = new Set([
    ...props.additionalColumns,
    ...Array.from(customFieldsFromRecipients)
  ]);

  // Create variables for all custom fields (excluding DDI)
  const customFieldVariables = Array.from(allCustomFields)
    .filter(column => {
      const lowerColumn = column.toLowerCase();
      return !lowerColumn.includes('ddi') && !lowerColumn.includes('country');
    })
    .map(column => ({
      name: `{{${column.toUpperCase()}}}`,
      description: `Valor da coluna ${column}`,
    }));

  return [...baseVariables, ...customFieldVariables];
});

// Template variables for the WhatsApp editor

const handleFileSelect = event => {
  const files = event.target.files;
  if (files && files.length > 0) {
    // WhatsApp allows only one media per message
    if (props.attachments.length > 0) {
      fileSizeAlertMessage.value = 'WhatsApp permite apenas um arquivo de mídia por mensagem. Por favor, remova o arquivo atual antes de adicionar outro.';
      showFileSizeAlert.value = true;
      event.target.value = null;
      return;
    }

    // Validate file size using dynamic limit from environment
    const file = files[0]; // Only take the first file for WhatsApp
    const maxSize = fileSizeLimit.value * 1024 * 1024; // Convert MB to bytes
    console.log('fileSizeLimit')
    console.log(fileSizeLimit)
    console.log("MaxSize")
    console.log(maxSize)

    if (file.size > maxSize) {
      fileSizeAlertMessage.value = `Arquivo muito grande. O limite para qualquer arquivo é ${fileSizeLimit.value}MB.`;
      showFileSizeAlert.value = true;
      event.target.value = null;
      return;
    }

    // Check if it's an audio file and there's existing text or variations
    if (isAudioFile(file.type) && (message.value.trim() || selectedVariations.value.length > 0)) {
      pendingAudioFile.value = file;
      showAudioConfirmModal.value = true;
      event.target.value = null;
      return;
    }

    emit('file-upload', [file]); // Only send one file
  }
  // Reset the input
  event.target.value = null;
};

const dragCounter = ref(0);

// Show overlay when files are being dragged anywhere or directly over component
const showDragOverlay = computed(() => {
  return props.isDraggingFiles || isDraggingOver.value;
});

const handleDragOver = event => {
  event.preventDefault();
  event.stopPropagation();
};

const handleDragEnter = event => {
  event.preventDefault();
  event.stopPropagation();
  dragCounter.value++;
  isDraggingOver.value = true;
};

const handleDragLeave = event => {
  event.preventDefault();
  event.stopPropagation();
  dragCounter.value--;
  if (dragCounter.value === 0) {
    isDraggingOver.value = false;
  }
};

const handleDrop = event => {
  event.preventDefault();
  event.stopPropagation(); // Keep this to prevent parent from also processing the drop
  dragCounter.value = 0;
  isDraggingOver.value = false;
  const files = event.dataTransfer.files;
  if (files && files.length > 0) {
    // WhatsApp allows only one media per message
    if (props.attachments.length > 0) {
      fileSizeAlertMessage.value = 'WhatsApp permite apenas um arquivo de mídia por mensagem. Por favor, remova o arquivo atual antes de adicionar outro.';
      showFileSizeAlert.value = true;
      return;
    }

    // Validate file size using dynamic limit from environment
    const file = files[0]; // Only take the first file for WhatsApp
    const maxSize = fileSizeLimit.value * 1024 * 1024; // Convert MB to bytes
        console.log('fileSizeLimit')
    console.log(fileSizeLimit.value)
    console.log("MaxSize")
    console.log(maxSize)

    if (file.size > maxSize) {
      fileSizeAlertMessage.value = `Arquivo muito grande. O limite para qualquer arquivo é ${fileSizeLimit.value}MB.`;
      showFileSizeAlert.value = true;
      return;
    }

    // Check if it's an audio file and there's existing text or variations
    if (isAudioFile(file.type) && (message.value.trim() || selectedVariations.value.length > 0)) {
      pendingAudioFile.value = file;
      showAudioConfirmModal.value = true;
      return;
    }

    emit('file-upload', [file]); // Only send one file
  }
};

const messageInputRef = ref(null);


// Variable and emoji insertion is now handled by WhatsApp editor component

const togglePreview = () => {
  emit('toggle-preview');
};

const formatFileSize = sizeInBytes => {
  if (sizeInBytes < 1024) {
    return `${sizeInBytes} B`;
  }
  if (sizeInBytes < 1024 * 1024) {
    return `${(sizeInBytes / 1024).toFixed(1)} KB`;
  }
  return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Get message for preview (either variation or regular message)
const messageWithVariablesReplaced = computed(() => {
  // If audio attachment is present, return empty string (audio-only mode)
  if (hasAudioAttachment.value) {
    return '';
  }

  // If we have variations, show the previewing variation
  if (generatedVariations.value.length > 0 && previewingVariationId.value) {
    const previewingVar = generatedVariations.value.find(
      v => v.id === previewingVariationId.value
    );
    if (previewingVar) {
      return previewingVar.text;
    }
  }

  // No variations - show what user is typing
  return message.value || '';
});

// Emoji picker and variables dropdowns are now handled by WhatsApp editor component

// Text editing is now handled by WhatsAppEditor component

// Authentication helper function
const getCookie = name => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
    };
  } catch (e) {
    console.error('Erro ao ler o cookie:', e);
    return {};
  }
};

// AI Rewrite functions
const canRewriteWithAI = computed(() => {
  return message.value && message.value.trim().length > 0;
});

// Add a ref to control showing variations interface
const forceShowVariations = ref(false);

const showVariationsInterface = computed(() => {
  return isGeneratingVariations.value || forceShowVariations.value;
});

// Alert modal for minimum text requirement
const showMinTextAlert = ref(false);
const minTextAlertMessage = ref('');

// Variation warning - always show the warning modal
const shouldShowVariationWarning = () => {
  return true; // Always show the warning
};

const confirmGenerateVariations = () => {
  showVariationWarning.value = false;
  acceptedTerms.value = false;
  clearTermsTimer();
  generateVariations();
};

const cancelVariationWarning = () => {
  showVariationWarning.value = false;
  acceptedTerms.value = false;
  clearTermsTimer();
};

const openVariationExplanation = () => {
  // Check if message is empty or too short
  if (!message.value || message.value.trim().length < 20) {
    minTextAlertMessage.value = 'Mensagem muito curta para gerar variações do texto.';
    showMinTextAlert.value = true;
    return;
  }

  // If we have selected variations, show them. Otherwise check for warning
  if (selectedVariations.value.length > 0) {
    forceShowVariations.value = true;
    // Set the first variation as previewed if not already set
    if (!previewingVariationId.value && generatedVariations.value.length > 0) {
      previewingVariationId.value = generatedVariations.value[0].id;
    }
  } else if (shouldShowVariationWarning()) {
    showVariationWarning.value = true;
    startTermsCountdown();
  } else {
    generateVariations();
  }
};

const cancelVariations = () => {
  // Return to input mode without clearing variations
  forceShowVariations.value = false;
  isGeneratingVariations.value = false;
  editingVariation.value = null;
  previewingVariationId.value = null;
};

const previewVariation = (variationId) => {
  previewingVariationId.value = variationId;
};

const toggleVariationSelection = variationId => {
  const index = selectedVariations.value.indexOf(variationId);
  if (index > -1) {
    selectedVariations.value.splice(index, 1);
  } else {
    selectedVariations.value.push(variationId);
  }
};

const startEditVariation = variationId => {
  editingVariation.value = variationId;
  nextTick(() => {
    const textarea = document.querySelector('.variation-edit-textarea');
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  });
};

const autoResizeTextarea = (event) => {
  const textarea = event.target;
  textarea.style.height = 'auto';
  textarea.style.height = textarea.scrollHeight + 'px';
};

const saveVariationEdit = (variationId, newText) => {
  const variation = generatedVariations.value.find(v => v.id === variationId);
  if (variation) {
    variation.text = newText;
  }
  editingVariation.value = null;
};

const cancelVariationEdit = () => {
  editingVariation.value = null;
};

const selectAllVariations = () => {
  selectedVariations.value = [...generatedVariations.value.map(v => v.id)];
};

const deselectAllVariations = () => {
  selectedVariations.value = [];
};

const generateMoreVariations = async () => {
  if (!canRewriteWithAI.value) return;

  isGeneratingVariations.value = true;
  variationsRetryCount.value = 0;

  while (variationsRetryCount.value < maxRetries) {
    try {
      const response = await fetch(
        '/api/v1/bulk_messages/generate_variations',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token':
              document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute('content') || '',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            message: message.value,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Validate the response (now expecting 10 variations)
      if (validateVariationsArray(data)) {
        // Add new variations to the existing list
        const newVariations = data.variations.map((text, index) => ({
          id: `var_${Date.now()}_${generatedVariations.value.length + index}`,
          text: text,
          selected: true, // New variations selected by default
        }));

        generatedVariations.value = [
          ...generatedVariations.value,
          ...newVariations,
        ];
        selectedVariations.value = [
          ...selectedVariations.value,
          ...newVariations.map(v => v.id),
        ];
        isGeneratingVariations.value = false;
        return;
      }
      console.warn('Invalid variations response, retrying...', data);
      variationsRetryCount.value++;

      if (variationsRetryCount.value < maxRetries) {
        await sleep(5000); // Wait 5 seconds before retry
      }
    } catch (error) {
      console.error('Erro ao gerar mais variações:', error);
      variationsRetryCount.value++;

      if (variationsRetryCount.value < maxRetries) {
        await sleep(5000); // Wait 5 seconds before retry
      }
    }
  }

  // If we get here, all retries failed
  isGeneratingVariations.value = false;
  alert(
    'Erro ao gerar mais variações. Todas as tentativas falharam. Tente novamente mais tarde.'
  );
};

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const validateVariationsArray = data => {
  if (!data || !Array.isArray(data.variations)) {
    return false;
  }

  // Check if we have at least 5 variations (more flexible)
  if (data.variations.length < 5) {
    return false;
  }

  // Check if all variations are non-empty strings
  return data.variations.every(
    variation => typeof variation === 'string' && variation.trim().length > 0
  );
};

const generateVariations = async () => {
  if (!canRewriteWithAI.value) return;

  isGeneratingVariations.value = true;
  variationsRetryCount.value = 0;

  while (variationsRetryCount.value < maxRetries) {
    try {
      const response = await fetch(
        '/api/v1/bulk_messages/generate_variations',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token':
              document
                .querySelector('meta[name="csrf-token\"]')
                ?.getAttribute('content') || '',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            message: message.value,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Validate the response
      if (validateVariationsArray(data)) {
        // Success! Set up variations with IDs and selection state (take first 10)
        const variationsToUse = data.variations.slice(0, 10);
        generatedVariations.value = variationsToUse.map((text, index) => ({
          id: `var_${Date.now()}_${index}`,
          text: text,
          selected: true, // All selected by default
        }));
        selectedVariations.value = [
          ...generatedVariations.value.map(v => v.id),
        ];
        // Set the first variation as the one being previewed
        previewingVariationId.value = generatedVariations.value[0].id;
        isGeneratingVariations.value = false;
        forceShowVariations.value = true; // Automatically show the variations interface
        return;
      }
      console.warn('Invalid variations response, retrying...', data);
      variationsRetryCount.value++;

      if (variationsRetryCount.value < maxRetries) {
        await sleep(5000); // Wait 5 seconds before retry
      }
    } catch (error) {
      console.error('Erro ao gerar variações:', error);
      variationsRetryCount.value++;

      if (variationsRetryCount.value < maxRetries) {
        await sleep(5000); // Wait 5 seconds before retry
      }
    }
  }

  // If we get here, all retries failed
  isGeneratingVariations.value = false;
  alert(
    'Erro ao gerar variações. Todas as tentativas falharam. Tente novamente mais tarde.'
  );
};

const approveAIRewrite = () => {
  message.value = aiRewrittenText.value;
  showAIApprovalModal.value = false;
  aiRewrittenText.value = '';
};

const rejectAIRewrite = () => {
  showAIApprovalModal.value = false;
  aiRewrittenText.value = '';
};

// Get the selected variation texts for external access
const getSelectedVariationTexts = () => {
  // If audio attachment is present, return empty array (audio-only mode)
  if (hasAudioAttachment.value) return [];
  
  if (selectedVariations.value.length === 0) return [];

  return generatedVariations.value
    .filter(v => selectedVariations.value.includes(v.id))
    .map(v => v.text);
};

// Randomly assign variations to recipients
const assignVariationsToRecipients = recipients => {
  const selectedTexts = getSelectedVariationTexts();
  if (selectedTexts.length === 0) return recipients;

  return recipients.map(recipient => ({
    ...recipient,
    assignedVariation:
      selectedTexts[Math.floor(Math.random() * selectedTexts.length)],
  }));
};


// Show generate more button based on variation limits
const shouldShowGenerateMoreButton = computed(() => {
  // Disable if 50+ selected variations OR 100+ total variations
  return selectedVariations.value.length < 50 && generatedVariations.value.length < 100;
});

// Check if all variations are selected
const areAllVariationsSelected = computed(() => {
  return generatedVariations.value.length > 0 &&
         selectedVariations.value.length === generatedVariations.value.length;
});

// Get the index of the previewing variation (1-based for display)
const previewingVariationIndex = computed(() => {
  if (!previewingVariationId.value) return 0;
  const index = generatedVariations.value.findIndex(
    v => v.id === previewingVariationId.value
  );
  return index >= 0 ? index + 1 : 0;
});

// Toggle selection based on current state
const toggleSelectAll = () => {
  if (areAllVariationsSelected.value) {
    deselectAllVariations();
  } else {
    selectAllVariations();
  }
};

// Handle alert close
const handleAlertClose = () => {
  showFileSizeAlert.value = false;
};

// Handle audio confirmation
const handleAudioConfirm = () => {
  // Clear message and variations
  message.value = '';
  selectedVariations.value = [];
  generatedVariations.value = [];
  forceShowVariations.value = false;
  previewingVariationId.value = null;

  // Upload the pending audio file
  if (pendingAudioFile.value) {
    emit('file-upload', [pendingAudioFile.value]);
    pendingAudioFile.value = null;
  }

  showAudioConfirmModal.value = false;
};

const handleAudioCancel = () => {
  pendingAudioFile.value = null;
  showAudioConfirmModal.value = false;
};

// Watch for component mount to restore state and ensure badge visibility
onMounted(() => {
  // Restore state from props if available
  if (props.initialVariationsState) {
    if (props.initialVariationsState.generatedVariations?.length > 0) {
      generatedVariations.value = [...props.initialVariationsState.generatedVariations];
    }
    if (props.initialVariationsState.selectedVariations?.length > 0) {
      selectedVariations.value = [...props.initialVariationsState.selectedVariations];
      // Don't auto-show the variations interface, just show the badge
      forceShowVariations.value = false;
    }
  }
  
  // Force a re-evaluation of the badge display when component mounts
  if (selectedVariations.value.length > 0) {
    // Trigger a small delay to ensure DOM is ready
    nextTick(() => {
      // This will ensure the badge is visible
    });
  }
});

// Get the message to show in preview (variation or typed message)
const getPreviewMessage = () => {
  if (generatedVariations.value.length > 0 && previewingVariationId.value) {
    const previewingVar = generatedVariations.value.find(
      v => v.id === previewingVariationId.value
    );
    if (previewingVar) {
      return previewingVar.text;
    }
  }
  return message.value || '';
};

// Expose reactive refs and functions for parent components to access
defineExpose({
  generatedVariations,
  selectedVariations,
  isGeneratingVariations,
  getSelectedVariationTexts,
  assignVariationsToRecipients,
  previewingVariationId,
  getPreviewMessage,
});
</script>

<script>
export default {
  components: {
    AlertResponza,
    WhatsAppEditor,
  },
};
</script>

<template>
  <div
    class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-1 overflow-visible w-full pb-2"
  >
    <!-- Variations Interface (shown when generating or variations exist) -->
    <div
      v-if="showVariationsInterface"
      class="variations-interface p-4 pb-2 bg-n-background"
    >
      <!-- Loading State -->
      <div v-if="isGeneratingVariations" class="text-center py-8 ">
        <div class="relative w-16 h-16 mx-auto mb-4">
          <div
            class="absolute inset-0 border-4 border-n-slate-3 rounded-full animate-spin border-t-n-iris-9"
          />
          <div
            class="absolute inset-2 bg-n-iris-9 rounded-full animate-pulse flex items-center justify-center"
          >
            <i class="i-lucide-sparkles text-white" />
          </div>
        </div>
        <h4
          class="text-lg font-medium text-n-slate-12 dark:text-n-slate-12 mb-2"
        >
          Gerando variações...
        </h4>
        <div
          class="w-64 bg-n-slate-3 dark:bg-n-slate-7 rounded-full h-2 mx-auto"
        >
          <div
            class="bg-n-iris-9 h-2 rounded-full transition-all duration-500"
            :style="{
              width: `${((variationsRetryCount + 1) / maxRetries) * 100}%`,
            }"
          />
        </div>
      </div>

      <!-- Generated Variations List -->
      <div v-else-if="generatedVariations.length > 0" class="space-y-4">
        <!-- Show selected image if any -->
        <div
          v-if="attachments.length > 0"
          class="border border-n-slate-3 dark:border-n-slate-7 rounded-lg p-3 bg-n-slate-1 dark:bg-n-slate-2"
        >
          <div class="flex items-center gap-3">
            <div class="flex-shrink-0">
              <template v-if="isImageFile(attachments[0].type)">
                <img
                  :src="attachments[0].url"
                  :alt="attachments[0].name"
                  class="w-16 h-16 object-cover rounded-lg"
                />
              </template>
              <template v-else-if="isVideoFile(attachments[0].type)">
                <video
                  :src="attachments[0].url"
                  class="w-16 h-16 object-cover rounded-lg"
                />
              </template>
              <template v-else-if="isAudioFile(attachments[0].type)">
                <div
                  class="w-16 h-16 flex items-center justify-center bg-n-slate-3 rounded-lg"
                >
                  <i class="i-lucide-volume-2 text-n-slate-9 text-xl" />
                </div>
              </template>
              <template v-else>
                <div
                  class="w-16 h-16 flex items-center justify-center bg-n-slate-3 rounded-lg"
                >
                  <i class="i-lucide-file text-n-slate-9 text-xl" />
                </div>
              </template>
            </div>
            <div class="flex-grow">
              <p
                class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12"
              >
                {{ attachments[0].name }}
              </p>
              <p class="text-xs text-n-slate-11 dark:text-n-slate-11">
                Este arquivo será enviado com todas as variações de texto
              </p>
            </div>
          </div>
        </div>

        <!-- Toolbar with all action buttons -->
        <div class="flex items-center gap-2 flex-wrap mb-4">
          <!-- Return to editing button -->
          <Button
            icon="i-lucide-arrow-left"
            variant="faded"
            color="slate"
            size="sm"
            label="Voltar"
            @click="cancelVariations"
          />

          <!-- Selection control -->
          <Button
            variant="faded"
            color="slate"
            size="sm"
            :label="areAllVariationsSelected ? 'Limpar seleção' : 'Selecionar todos'"
            @click="toggleSelectAll"
          />

          <!-- Generate more button with count -->
          <div class="flex items-center gap-2">
            <button
              :disabled="
                isGeneratingVariations || !shouldShowGenerateMoreButton
              "
              class="ai-generate-button relative px-3 py-1.5 text-sm font-medium rounded-lg text-white transition-all duration-300 whitespace-nowrap overflow-hidden flex items-center gap-2"
              :class="[
                shouldShowGenerateMoreButton && !isGeneratingVariations
                  ? 'hover:scale-105'
                  : 'opacity-50 cursor-not-allowed',
              ]"
              @click="generateMoreVariations"
            >
              <i
                v-if="isGeneratingVariations"
                class="animate-spin i-lucide-loader text-sm"
              />
              <i v-else class="i-lucide-sparkles text-sm" />
              <span>{{
                isGeneratingVariations ? 'Gerando...' : 'Gerar +10'
              }}</span>
            </button>
            <span class="text-sm text-n-slate-11">
              {{ selectedVariations.length }}/50 selecionadas
            </span>
          </div>
        </div>

        <!-- Variations List -->
        <div class="max-h-80 overflow-y-auto space-y-3">
          <div
            v-for="variation in generatedVariations"
            :key="variation.id"
            :class="[
              'group flex items-start gap-2 p-2 rounded-lg transition-colors',
              selectedVariations.includes(variation.id)
                ? 'bg-woot-50 dark:bg-woot-100/10 hover:bg-woot-100 dark:hover:bg-woot-100/20'
                : 'hover:bg-n-slate-1 dark:hover:bg-n-slate-2'
            ]"
          >
            <!-- Text Content (left side, flex-grow) -->
            <div class="flex-grow min-w-0">
              <div v-if="editingVariation === variation.id">
                <textarea
                  :value="variation.text"
                  class="variation-edit-textarea w-full p-2 border border-n-slate-3 dark:border-n-slate-8 rounded-lg bg-n-background dark:bg-n-slate-1 text-n-slate-12 dark:text-n-slate-12 resize-y text-sm"
                  rows="2"
                  @input="variation.text = $event.target.value; autoResizeTextarea($event)"
                />
              </div>
              <div v-else>
                <p
                  class="text-sm text-n-slate-12 dark:text-n-slate-12 cursor-pointer py-1 px-2 rounded variation-text-truncate"
                  title="Clique para selecionar/deselecionar esta variação"
                  @click="toggleVariationSelection(variation.id)"
                >
                  {{ variation.text }}
                </p>
              </div>
            </div>

            <!-- Action Buttons (right side, vertical stack) -->
            <div class="flex-shrink-0 flex flex-col items-center gap-1">
              <template v-if="editingVariation === variation.id">
                <!-- Cancel button (when editing) -->
                <button
                  class="w-8 h-8 p-0 flex items-center justify-center text-n-slate-9 bg-white dark:bg-white rounded-lg transition-colors"
                  title="Cancelar"
                  @click="cancelVariationEdit"
                >
                  <i class="i-lucide-undo-2 text-base" />
                </button>
                <!-- Confirm button (when editing) -->
                <button
                  class="w-8 h-8 p-0 flex items-center justify-center text-n-teal-9 bg-white dark:bg-white rounded-lg transition-colors"
                  title="Salvar"
                  @click="saveVariationEdit(variation.id, variation.text)"
                >
                  <i class="i-lucide-check text-base" />
                </button>
              </template>
              <template v-else>
                <!-- Select/Deselect button -->
                <button
                  type="button"
                  class="w-8 h-8 p-0 flex items-center justify-center rounded-lg transition-all"
                  :class="selectedVariations.includes(variation.id)
                    ? 'bg-woot-500 text-white'
                    : 'bg-white text-n-slate-9 hover:text-n-slate-12'"
                  :title="selectedVariations.includes(variation.id) ? 'Desmarcar' : 'Selecionar'"
                  @click="toggleVariationSelection(variation.id)"
                >
                  <i :class="selectedVariations.includes(variation.id) ? 'i-lucide-check' : 'i-lucide-square'" class="text-base" />
                </button>
                <!-- Edit button -->
                <button
                  class="w-8 h-8 p-0 flex items-center justify-center text-n-slate-9 bg-white dark:bg-white rounded-lg transition-colors"
                  title="Editar"
                  @click="startEditVariation(variation.id)"
                >
                  <i class="i-lucide-edit text-base" />
                </button>
                <!-- Preview button -->
                <button
                  type="button"
                  class="w-8 h-8 p-0 flex items-center justify-center rounded-lg transition-all"
                  :class="previewingVariationId === variation.id
                    ? 'bg-woot-500 text-white'
                    : 'bg-white text-n-slate-9 hover:text-n-slate-12'"
                  title="Ver prévia"
                  @click.stop="previewVariation(variation.id)"
                >
                  <i class="i-lucide-eye text-base" />
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Message Input Area (shown when not in variations mode) -->
    <div
      v-else
      class="message-input-area relative"
      :class="[{ hidden: preview }]"
      @dragover="handleDragOver"
      @dragenter="handleDragEnter"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <!-- Drag & Drop Overlay -->
      <div
        v-if="showDragOverlay"
        class="absolute inset-0 z-50 flex items-center justify-center pointer-events-none"
      >
        <div class="absolute inset-0 bg-woot-50/90 dark:bg-woot-900/90 rounded-lg border-2 border-dotted border-woot-600"></div>
        <div class="relative text-center">
          <div class="mb-4 flex justify-center">
            <i class="i-lucide-upload-cloud text-6xl text-woot-600" />
          </div>
          <p class="font-semibold text-xl text-woot-600">
            Solte arquivos aqui
          </p>
        </div>
      </div>

      <div
        class="rounded-lg overflow-hidden"
      >
        <!-- Image Preview Area (like WhatsApp) -->
        <div v-if="attachments.length > 0" class="relative">
          <div
            v-for="(attachment, index) in attachments"
            :key="attachment.id"
            class="relative"
          >
            <template v-if="isImageFile(attachment.type)">
              <img
                :src="attachment.url"
                :alt="attachment.name"
                class="w-full max-h-64 object-cover"
              />
            </template>
            <template v-else-if="isVideoFile(attachment.type)">
              <video
                :src="attachment.url"
                class="w-full max-h-64 object-cover"
                controls
              />
            </template>
            <template v-else-if="isAudioFile(attachment.type)">
              <div
                class="flex items-center gap-3 p-4 bg-n-slate-1 dark:bg-n-slate-2 rounded-t-lg"
              >
                <div
                  class="w-12 h-12 flex items-center justify-center bg-n-slate-3 dark:bg-n-slate-7 rounded"
                >
                  <i
                    class="i-lucide-volume-2 text-n-slate-9 dark:text-n-slate-11 text-xl"
                  />
                </div>
                <div class="flex-grow">
                  <div class="font-medium text-n-slate-12 dark:text-n-slate-12">
                    {{ attachment.name }}
                  </div>
                  <div class="text-sm text-n-slate-9 dark:text-n-slate-11">
                    {{ formatFileSize(attachment.size) }}
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <div
                class="flex items-center gap-3 p-4 bg-n-slate-1 dark:bg-n-slate-2 rounded-t-lg"
              >
                <div
                  class="w-12 h-12 flex items-center justify-center bg-n-slate-3 dark:bg-n-slate-7 rounded"
                >
                  <i
                    class="i-lucide-file text-n-slate-9 dark:text-n-slate-11 text-xl"
                  />
                </div>
                <div class="flex-grow">
                  <div class="font-medium text-n-slate-12 dark:text-n-slate-12">
                    {{ attachment.name }}
                  </div>
                  <div class="text-sm text-n-slate-9 dark:text-n-slate-11">
                    {{ formatFileSize(attachment.size) }}
                  </div>
                </div>
              </div>
            </template>

            <!-- Remove button -->
            <button
              class="absolute top-2 right-2 w-9 h-9 bg-n-ruby-9 hover:bg-n-ruby-10 text-white rounded-full flex items-center justify-center transition-all shadow-lg hover:shadow-xl hover:scale-110"
              title="Remover arquivo"
              @click="emit('remove-attachment', index)"
            >
              <i class="i-lucide-trash-2 text-base" />
            </button>
          </div>
        </div>

        <div class="relative">
          <!-- WhatsApp Text Editor -->
          <WhatsAppEditor
            ref="messageInputRef"
            v-model="message"
            :disabled="hasAudioAttachment"
            :placeholder="hasAudioAttachment ? 'Mensagens de áudio não permitem texto adicional' : (attachments.length > 0 ? 'Adicione uma legenda...' : 'Digite sua mensagem...')"
            :template-variables="templateVariables"
            :has-audio-attachment="hasAudioAttachment"
            :has-selected-variations="selectedVariations.length > 0"
            :has-messenger-inbox="hasMessengerInbox"
            :show-template-variables="showTemplateVariables"
            :show-emoji-picker="showEmojiPicker"
            show-flag-preview
            @toggle-variables="showTemplateVariables = !showTemplateVariables"
            @toggle-emoji="showEmojiPicker = !showEmojiPicker"
            @attach-file="$refs.fileInput.click()"
            @generate-ai-variations="openVariationExplanation"
          />
          
          <!-- Hidden file input -->
          <input
            ref="fileInput"
            type="file"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
            class="hidden"
            @change="handleFileSelect"
          />
        </div>


      </div>

      <!-- Variable autocomplete is now handled by WhatsAppEditor -->

      <!-- Toolbar -->
      <div
        class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 mt-2"
      >
        <div class="flex items-center gap-2 w-full sm:w-auto justify-end">
          <!-- Preview Toggle -->
          <Button
            v-if="!hidePreviewButton"
            icon="i-lucide-eye"
            variant="outline"
            color="blue"
            size="xs"
            label="Pré-visualizar"
            @click="togglePreview"
          />

        </div>
      </div>
    </div>

    <!-- WhatsApp Message Preview -->
    <div v-if="preview" class="message-preview p-4">
      <div class="flex justify-between items-center mb-4">
        <div>
          <h4 class="text-n-slate-12 dark:text-n-slate-12 font-medium">
            Pré-visualização da mensagem
          </h4>
          <p
            v-if="generatedVariations.length > 0 && previewingVariationIndex > 0"
            class="text-xs text-n-slate-11 dark:text-n-slate-11 mt-1"
          >
            Prévia da variação {{ previewingVariationIndex }} de {{ generatedVariations.length }}
          </p>
        </div>
        <Button
          icon="i-lucide-edit"
          variant="outline"
          color="blue"
          size="xs"
          label="Editar"
          @click="togglePreview"
        />
      </div>

      <MessagePreview
        type="whatsapp"
        :message="messageWithVariablesReplaced"
        :recipient-name="
          recipients.length > 0 && recipients[0].name
            ? recipients[0].name
            : 'Nome do Cliente'
        "
        :attachments="attachments"
        :recipients="recipients"
        :additional-columns="additionalColumns"
        title=""
      />

      <!-- Show information about all selected variations -->
      <div
        v-if="selectedVariations.length > 1"
        class="mt-4 p-3 bg-n-iris-2 border border-n-iris-6 rounded-lg"
      >
        <div class="flex items-center gap-2 mb-2">
          <i class="i-lucide-info text-n-iris-9 text-sm" />
          <span class="text-sm font-medium text-n-iris-11">{{ selectedVariations.length }} variações serão enviadas</span>
        </div>
        <p class="text-xs text-n-iris-11">
          Cada destinatário receberá uma variação diferente da mensagem para
          personalizar o envio.
        </p>
      </div>
    </div>

    <!-- AI Approval Modal -->
    <div
      v-if="showAIApprovalModal"
      class="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center z-50"
      @click.self="rejectAIRewrite"
    >
      <div
        class="bg-n-background dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden border border-n-slate-3 dark:border-slate-600"
      >
        <div class="p-6 border-b border-n-slate-3 dark:border-slate-600">
          <h3 class="text-lg font-semibold text-n-slate-12 dark:text-slate-100">
            Texto reescrito pela IA
          </h3>
          <p class="text-sm text-n-slate-11 dark:text-slate-300 mt-1">
            Deseja usar o texto reescrito pela IA?
          </p>
        </div>

        <div class="p-6 max-h-96 overflow-y-auto">
          <div class="space-y-4">
            <div>
              <label
                class="block text-sm font-medium text-n-slate-11 dark:text-slate-300 mb-2"
                >Texto original:</label>
              <div
                class="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border-2 border-red-200 dark:border-red-800/50 shadow-sm"
              >
                <p
                  class="text-slate-800 dark:text-red-100 whitespace-pre-wrap font-medium"
                >
                  {{ message }}
                </p>
              </div>
            </div>

            <div>
              <label
                class="block text-sm font-medium text-n-slate-11 dark:text-slate-300 mb-2"
                >Texto reescrito pela IA:</label>
              <div
                class="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-green-200 dark:border-green-800/50 shadow-sm"
              >
                <p
                  class="text-slate-800 dark:text-green-100 whitespace-pre-wrap font-medium"
                >
                  {{ aiRewrittenText }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          class="p-6 border-t border-n-slate-3 dark:border-slate-600 flex justify-end space-x-3"
        >
          <button
            class="px-4 py-2 text-n-slate-11 text-white bg-red-500 hover:bg-red-800 dark:text-slate-300 bg-n-slate-1 dark:bg-slate-700 border border-n-slate-3 dark:border-slate-600 rounded-lg hover:bg-n-slate-2 dark:hover:bg-slate-600 transition-colors"
            @click="rejectAIRewrite"
          >
            Manter original
          </button>
          <button
            class="ai-accept-button px-4 py-2 rounded-lg font-medium transition-all shadow-md hover:shadow-lg"
            style="
              background: linear-gradient(
                135deg,
                #3b82f6 0%,
                #1d4ed8 100%
              ) !important;
              color: white !important;
              border: none !important;
            "
            @click="approveAIRewrite"
          >
            Aceitar Mudanças
          </button>
        </div>
      </div>
    </div>

    <!-- File Size Alert Modal -->
    <AlertResponza
      :show="showFileSizeAlert"
      type="warn"
      title="Arquivo muito grande"
      :message="fileSizeAlertMessage"
      @close="handleAlertClose"
    />

    <!-- Audio Confirmation Modal -->
    <AlertResponza
      :show="showAudioConfirmModal"
      type="confirmation"
      title="Anexos de áudio não suportam texto"
      message="O texto da mensagem atual e as variações serão excluídos se o áudio for importado. Prosseguir?"
      @confirm="handleAudioConfirm"
      @cancel="handleAudioCancel"
    />

    <!-- Minimum Text Alert Modal -->
    <AlertResponza
      :show="showMinTextAlert"
      type="warn"
      title="Texto insuficiente"
      :message="minTextAlertMessage"
      @close="showMinTextAlert = false"
    />

    <!-- Modal de Termo de Responsabilidade para Variações -->
    <div
      v-if="showVariationWarning"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-slate-12/20 dark:bg-n-slate-1/70"
    >
      <div
        class="bg-n-solid-2 rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden"
      >
        <!-- Header -->
        <div
          class="px-5 py-4 border-b border-n-strong flex items-center gap-3"
        >
          <h3 class="text-xl font-semibold text-n-slate-12">
            Termo de Responsabilidade
          </h3>
        </div>

        <!-- Body -->
        <div class="px-6 py-4">
          <p class="text-n-slate-12 text-sm leading-relaxed font-medium">
            Ao utilizar a geração de variações por inteligência artificial, você
            declara estar ciente que:
          </p>
          <ul class="mt-4 space-y-2 text-sm text-n-slate-11 list-disc pl-4">
            <li>As variações geradas podem conter erros gramaticais, de concordância ou ortografia</li>
            <li>O sentido original do texto pode ser alterado de forma não intencional</li>
            <li>Informações podem ser modificadas, omitidas ou apresentadas de forma imprecisa</li>
          </ul>

          <div class="mt-5 p-4 bg-n-amber-3 rounded-lg border border-n-amber-6">
            <p class="text-sm text-n-amber-11 font-medium">
              Ao continuar, você assume total responsabilidade por revisar cada
              variação gerada e garantir que o conteúdo esteja de acordo com o
              que deseja enviar.
            </p>
          </div>

          <!-- Checkbox de concordância -->
          <label
            class="flex items-center gap-3 mt-5 group"
            :class="canAcceptTerms ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'"
          >
            <input
              v-model="acceptedTerms"
              type="checkbox"
              :disabled="!canAcceptTerms"
              class="w-4 h-4 rounded border-n-slate-6 text-n-iris-9 focus:ring-n-iris-9 disabled:cursor-not-allowed"
            />
            <span
              class="text-sm text-n-slate-11 transition-colors"
              :class="canAcceptTerms ? 'group-hover:text-n-slate-12' : ''"
            >
              <template v-if="canAcceptTerms">
                Li e concordo com os termos acima.
              </template>
              <template v-else>
                Aguarde {{ termsCountdown }}s para confirmar...
              </template>
            </span>
          </label>
        </div>

        <!-- Footer -->
        <div
          class="px-6 py-4 border-t border-n-strong flex justify-end gap-3"
        >
          <button
            class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-slate-3 rounded-lg transition-colors"
            @click="cancelVariationWarning"
          >
            Cancelar
          </button>
          <button
            :disabled="!acceptedTerms"
            :class="[
              'px-5 py-2 text-sm font-semibold rounded-lg transition-colors',
              acceptedTerms
                ? 'text-white bg-n-iris-9 hover:bg-n-iris-10 cursor-pointer'
                : 'text-n-slate-9 bg-n-slate-4 cursor-not-allowed'
            ]"
            @click="confirmGenerateVariations"
          >
            Continuar
          </button>
        </div>
      </div>
    </div>

    <!-- Selection Info (shown in both views when variations are selected) -->
    <div
      v-if="selectedVariations.length > 0"
      class="bg-woot-50 dark:bg-woot-800 rounded-lg mx-2 p-2"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <i
            class="i-lucide-info text-n-slate-12 dark:text-n-slate-12 text-base"
          />
          <div class="text-sm text-n-slate-11 dark:text-n-slate-12">
            <p class="b-none">
              <strong>{{ selectedVariations.length }}</strong> variações selecionadas para
              <strong>{{ recipients.length }}</strong> destinatários.  
            <span v-if="attachments.length > 0">
              A imagem será mantida em todas as variações.
            </span>
            </p>
            <p class="text-xs text-n-slate-10 dark:text-n-slate-11 mt-1 italic">
              A IA pode cometer erros. Revise as variações antes de enviar.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom styling for WhatsApp Editor in MessageComposer */

/* Smooth transitions for drag overlay */
.message-input-area {
  transition: all 0.2s ease;
}

.ai-accept-button {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
  color: white !important;
  border: none !important;
}

.dark .ai-accept-button {
  background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%) !important;
}

.dark .ai-accept-button:hover {
  background: linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%) !important;
}

/* AI Generate Button with shine effect */
.ai-generate-button {
  background: #1f93ff;
  position: relative;
}

.ai-generate-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -150%;
  width: 150%;
  height: 100%;
  background: linear-gradient(90deg, 
    transparent 0%,
    rgba(147, 197, 253, 0.3) 30%,
    rgba(255, 255, 255, 0.5) 50%,
    rgba(147, 197, 253, 0.3) 70%,
    transparent 100%
  );
  transform: skewX(-25deg);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.ai-generate-button:not(:disabled):hover {
  filter: brightness(115%);
}

.ai-generate-button:not(:disabled):hover::before {
  opacity: 1;
  animation: shine-sweep 1.2s ease;
}

@keyframes shine-sweep {
  0% {
    left: -150%;
  }
  100% {
    left: 150%;
  }
}

.ai-generate-button:disabled {
  background: #9ca3af;
  filter: none;
}

.ai-generate-button:disabled::before {
  display: none;
}

/* Animated gradient border */
@keyframes gradient-x {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.animate-gradient-x {
  background-size: 400% 400%;
  animation: gradient-x 3s ease infinite;
}

/* Variation text truncation */
.variation-text-truncate {
  word-break: break-word;
  white-space: pre-wrap;
  max-height: 7.5em;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
}

.group:hover .variation-text-truncate {
  max-height: 500px;
  transition: max-height 0.5s ease-in;
}

</style>
